import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DownloadBackupDTO } from '@gipi-pages/backup/models/download-backup.dto';
import { DownloadBackupService } from '@gipi-pages/backup/services/download-backup.service';
import { TypeAuthorities } from '@gipi-ui/enums/enum-authorities.enum';
import { AlertService } from '@gipi-ui/services/alert.service';
import { ValidateAccessService } from '@gipi-ui/services/validate-access.service';
import { ArrayUtil } from '@gipi-ui/utils/array.util';
import { ObjectUtil } from '@gipi-ui/utils/object.util';
import { BehaviorSubject, Observable, of } from 'rxjs';

@Component({
    selector: 'gipi-download-backup-dialog',
    templateUrl: './download-backup-dialog.component.html',
    styleUrls: ['./download-backup-dialog.component.scss']
})
export class DownloadBackupDialogComponent implements OnInit {

    public loading$ = new BehaviorSubject(false);

    public listBackup$ = new BehaviorSubject<DownloadBackupDTO[]>([]);

    public get textQuantityBackup$(): Observable<string> {
        const backups = this.listBackup$.getValue();
        if (ArrayUtil.isEmpty(backups)) {
            return of(`Nenhum backup disponível`);
        }
        return of(`${backups.length} ${backups.length === 1 ? 'backup disponível' : 'backups disponíveis'}`);
    }

    public get textQuantitySize$(): Observable<string> {
        const backups = this.listBackup$.getValue();
        const total = backups.reduce((sum, backup) => sum += backup.size, 0);
        return of(`${this.formatBytes(total)} total`);
    }

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _alertService: AlertService,
        private _service: DownloadBackupService,
        private _validateAccessService: ValidateAccessService,
        protected dialogRef: MatDialogRef<DownloadBackupDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { cpfOrCnpjClient: string, nameClient: string } = { cpfOrCnpjClient: '', nameClient: '' },
    ) { }

    ngOnInit(): void {
        this.findAll();
    }

    public close(): void {
        this.dialogRef.close();
    }

    public findAll(): void {
        try {
            this.loading$.next(true);
            this.listBackup$.next([]);

            this._service.findBackupByCnpj(this.data.cpfOrCnpjClient).toPromise().then(list => {
                this.listBackup$.next(list);
                this.loading$.next(false);
            }, error => {
                this.loading$.next(false);
                throw new Error(error);
            });
        } catch (e) {
            this.loading$.next(false);
            throw new Error(e);
        } finally {
            this._changeDetectorRef.detectChanges();
        }
    }

    public onHandleDownloadBackup(entity: DownloadBackupDTO): void {
        if (ObjectUtil.isNull(entity)) {
            this._alertService.addWarningMessage('Não foi possível realizar o download do arquivo');
            return;
        }
        this.loading$.next(true);

        this._service.downloadBackupFile(entity.key).subscribe((res) => {
            const blob = res.body!;

            // Pega o nome do arquivo do Content-Disposition
            const contentDisposition = res.headers.get('content-disposition');
            let filename = 'backup.zip';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match && match[1]) {
                    filename = match[1].trim(); // remove espaços no início/fim
                    filename = filename.replace(/[\u0000-\u001F\u007F]/g, ''); // remove caracteres de controle
                }
            }

            // garante que termina com .zip
            if (!filename.toLowerCase().endsWith('.zip')) {
                filename += '.zip';
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);

            this.loading$.next(false);
            this._alertService.addSuccessMessage('Download realizado com sucesso');
        }, error => {
            this.loading$.next(false);
            this._alertService.addErrorMessage(error);
        });
    }

    public extractFileName(key: string): string {
        const parts = key.split('/');
        return parts[parts.length - 1];
    }

    public formatDateTime(dateInp: Date): string {
        return new Date(dateInp).toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    public formatBytes(bytes: number, decimals = 2) {
        if (bytes === 0) return '0 Bytes';
        const kb = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(kb));
        return parseFloat((bytes / Math.pow(kb, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    public validateAccess(authorities: TypeAuthorities): boolean {
        return this._validateAccessService.validateAccess(authorities);
    }
}
